import { Component, ComponentFactoryResolver, ComponentRef, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IContextSetting, NetworkConstants, Person } from 'diwimamodelweb/lib';
import { deepCopy } from 'diwimamodelweb/lib/utils/obejct-diff';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';
import { FilterListComponent } from '../filter-list/filter-list.component';
import { GamerecordService } from '../gamerecord.service';
import { GenericDialogComponent } from '../generic-dialog/generic-dialog.component';
import { InjectComponentDirective } from '../inject-component.directive';
import { BaseSettingForm, Form, gOCategorySettingForm, gOLineTypesSettingForm, gOPolygonTypesSettingForm, PointOfInterestForm, PoiTypeSettingForm, SiteReferralSettingForm, usRolesSettingForm } from '../model/form';
import { ListItem, SelectableListItem } from '../model/list';
import { NavListItemComponent } from '../nav-list-item/nav-list-item.component';
import { PersonService } from '../person.service';
import { PoiService } from '../poi.service';
import { SettingGeoObjectTypeFormComponent } from '../setting-geo-object-type-form/setting-geo-object-type-form.component';
import { SettingSpeciesFormComponent } from '../setting-species-form/setting-species-form.component';
import { SettingsService } from '../settings.service';
import { UserFeedbackService } from '../user-feedback.service';
import { UserService } from '../user.service';
import { UtilizationOptionFormComponent } from '../utilization-option-form/utilization-option-form.component';

enum SettingsHandlerRoutes{
  BASE, FORM, VALUES_SELECT
}
/**
 * Component for handling swapping in desired views. Template contains a `<ng-template injectComp></ng-template>`
 * This is the root for holding "generic" views. Associated to settings-items (Context settings)
 */
@Component({
  selector: 'app-component-settings-handler',
  templateUrl: './component-settings-handler.component.html',
  styleUrls: ['./component-settings-handler.component.css', '../../app.component.scss'],
  // directives: [SettingGeoObjectTypeFormComponent]
})
export class ComponentSettingsHandlerComponent implements OnInit {

  showBack = false
  showAdd = false

  get isSubContext(): boolean {
    return this.settingsService.subContext?.value === true
  }
  data_saved=false
  newCreation=false
  title$ = new BehaviorSubject<string>("SETTINGS")

  contextSettings: Map<string, {setting: any, formRef: Type<any>, visible: boolean}> = new Map([
    [NetworkConstants.SETTING_HUNTING_METHODS, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_GEO_TYPE, {setting: undefined, formRef: PoiTypeSettingForm, visible: true}],
    [NetworkConstants.SETTING_GEO_LINE_TYPES, {setting: undefined, formRef: gOLineTypesSettingForm, visible: true}],
    [NetworkConstants.SETTING_GEO_POLYGON_TYPES, {setting: undefined, formRef: gOPolygonTypesSettingForm, visible: true}],
    [NetworkConstants.SETTING_GEO_CATEGORY, {setting: undefined, formRef: gOCategorySettingForm, visible: true}],
    [NetworkConstants.SETTING_GEO_USAGE_STATUS, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_SPECIES, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_COLLECTIVE_SPECIES, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_GR_LOCATION, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_GR_STATUS, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    // [NetworkConstants.SETTING_LAB_SAMPLE_LOCATION, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_LAB_SAMPLE_STATUS, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_LAB_SAMPLE_TYPE, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_GR_MEAT_QUALITY, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_GR_SHOT_QUALITY, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_UTILIZATION_OPTION, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_TROPY_TYPE, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_EVENT_INVITATION_SITE_REFERRAL, {setting: undefined, formRef: SiteReferralSettingForm, visible: false}],
    [NetworkConstants.SETTING_READOUT_GROUPS, {setting: undefined, formRef: BaseSettingForm, visible: true}],
    [NetworkConstants.SETTING_USER_ROLES, {setting: undefined, formRef: usRolesSettingForm, visible: true}], // NAR-5647
  ])

  currentState = SettingsHandlerRoutes.BASE
  oldSetting: any
  // for back an forth ... 
  navigationPath: any[] = []

  @ViewChild(InjectComponentDirective, {static: true}) view: InjectComponentDirective;
  @ViewChild("setting-geo-object-type-form") settingGeoObjectTypeForm: SettingGeoObjectTypeFormComponent

  componentRef: ComponentRef<any>|undefined = undefined
  viewContainerRef: ViewContainerRef|undefined = undefined 
  
  customRoles:any
  createRight = false
  updateRight = false

  constructor(
    private userFeedbackService: UserFeedbackService,
    private settingsService: SettingsService,
    private userService: UserService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private personService: PersonService,
    private gamerecordService: GamerecordService,
    private poiService: PoiService
  ) { }

  async ngOnInit() {
    const context = await firstValueFrom(this.settingsService.selectedUserContext$.pipe(
      skipWhile(el => !el),
      take(1)
    ))
    const rights = this.settingsService.rightsPercontext.get(context.identifier)
    this.createRight = !rights?.[NetworkConstants.ROUTE_CTX_SETTINGS]?.['C'] ?? false
    this.updateRight = !rights?.[NetworkConstants.ROUTE_CTX_SETTINGS]?.['U'] ?? false
    this.customRoles = context.modulesMap?.get('customRoles') ? true : false

    // if (this.customRoles) {
    //   this.contextSettings.set(NetworkConstants.SETTING_USER_ROLES,{setting: undefined, formRef: usRolesSettingForm, visible: true} )
    // }
    
    const titleTranstlated = await this.translateService.instant('COCKPIT.SETTINGS.TITLE')
    this.title$.next(titleTranstlated ? titleTranstlated : this.title$.value)
    
    this.settingsService.selectedUserContext$.subscribe((context) => {
      this.contextSettings.get(NetworkConstants.SETTING_EVENT_INVITATION_SITE_REFERRAL).visible = context && context.huntingEventManagement
    })
    
    await this.observeData()
    this.navigateToBaseSelect()
  }

  async injectAppropriateView(settingId: string|undefined, values: ListItem[]|undefined, form: Form|undefined) {
    if (!settingId) {
      // no setting selected reset selection
      this.oldSetting = undefined
      this.showBack = false
      this.showAdd = false
      const titleTranstlated = await this.translateService.instant('COCKPIT.SETTINGS.TITLE')
      this.title$.next(titleTranstlated ? titleTranstlated : this.title$.value)
    }
    if (settingId && !form) this.currentState = SettingsHandlerRoutes.VALUES_SELECT

    if (settingId && form) this.currentState = SettingsHandlerRoutes.FORM
    // reset before showing new ... 
    this.viewContainerRef?.clear()
    this.data_saved = false

    switch(this.currentState) {
      case(SettingsHandlerRoutes.BASE): 
      case(SettingsHandlerRoutes.VALUES_SELECT): {
        if (this.currentState == SettingsHandlerRoutes.BASE) {
          const titleTranstlated = await this.translateService.instant('COCKPIT.SETTINGS.TITLE')
          this.title$.next(titleTranstlated ? titleTranstlated : this.title$.value)
        }
        if (settingId) { 
          const t = await this.translateService.instant(`COCKPIT.SETTINGS.${settingId}`)
          this.title$.next(t?t:this.title$.value)
        
          // ..deep copy this thing
          this.oldSetting = deepCopy(this.contextSettings.get(settingId).setting)
          this.showBack = true
          this.showAdd = !this.isSubContext
        }
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(FilterListComponent)
        this.viewContainerRef = this.view.viewContainerRef
        this.componentRef = this.viewContainerRef.createComponent(componentFactory)
        this.componentRef.instance.data = values
        this.componentRef.instance.showAdd = false
        this.componentRef.instance.showFilter = false
        break
      }

      case (SettingsHandlerRoutes.FORM): {
        this.showAdd = false
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(form.componentRef)
        this.viewContainerRef = this.view.viewContainerRef
        this.componentRef = this.viewContainerRef.createComponent(componentFactory)
        this.componentRef.instance.data = form
        this.componentRef.instance.formBuilder = this.formBuilder,
        this.componentRef.instance.translateService = this.translateService
        // this.componentRef.instance.settingsService = this.settingsService
        break
      }
    }

  }

  async navigateToBaseSelect(settingId?: string|undefined) {
    let values:  any[] = []
    for (let [key, value] of this.contextSettings.entries()) {
      if (!value.visible) continue
      const title = await this.translateService.instant(`COCKPIT.SETTINGS.${key}`)
      const listItem = new SelectableListItem(
        title,
        "",
        value,
        NavListItemComponent
      )
      listItem.addListener(this)
      values.push(listItem)
    }

    if(!settingId){
    this.navigationPath.push({settingId: undefined, values, form: undefined})
    this.injectAppropriateView(undefined, values, undefined)
    }else{
      this.navigationPath.push({settingId: settingId, values, form: undefined})
      this.injectAppropriateView(settingId, values, undefined)
    }
  }

  async navBack(settingId?: string|undefined, values?: ListItem[]|undefined, form?: Form|undefined, afterDelete?:boolean|undefined) {
    
    this.navigationPath.pop()

    //if the data changes are saved and then it calls this function
    if(this.data_saved){
      switch(settingId){
        case NetworkConstants.SETTING_HUNTING_METHODS: {
          const data = this.navigationPath[this.navigationPath.length-1]
          this.injectAppropriateView(settingId, data.values, undefined)
          break;
        }
        case NetworkConstants.SETTING_GEO_TYPE: {
          const data = this.navigationPath[this.navigationPath.length-1]
          this.injectAppropriateView(settingId, data.values, undefined)
          break;
        }
        case NetworkConstants.SETTING_GEO_CATEGORY: {
          const data = this.navigationPath[this.navigationPath.length-1]
          this.injectAppropriateView(settingId, data.values, undefined)
          break;

        }
        default: {
          if(settingId != NetworkConstants.SETTING_GEO_CATEGORY && settingId != NetworkConstants.SETTING_HUNTING_METHODS){
            this.navigateToBaseSelect(settingId)
          break;
          }
        }
      }
      
    }else{

      //if the data changes are not saved
      if (this.navigationPath.length > 1) {

        let {settingId, values, form} = this.navigationPath[this.navigationPath.length -1]

        ///if it is a geoObjectTypes
        if (settingId=="geoObjectTypes"){
          var a =(<HTMLInputElement>document.getElementById("chooseIconName"))?.textContent
          var inputvalue = (<HTMLInputElement>document.getElementById("actualIconName"))?.value
          var oldIconName = (<HTMLInputElement>document.getElementById("oldIconName"))?.textContent
          
          if(this.newCreation){
          }else{
            if(!afterDelete){
              if( oldIconName == inputvalue){
                this.injectAppropriateView(settingId, values, form)
              } else {
                if (this.updateRight) {
                  const title = this.translateService.instant('title_unsaved_changes')
                  const message = this.translateService.instant('msg_unsaved_changes')
                  this.userFeedbackService.showDialog.emit(
                    {
                      entryComponent: GenericDialogComponent,
                      title,
                      message,
                      cancleCallback: async () => {
                        const sbSuccessSave = this.translateService.instant('changes_dismissed') 
                        this.userFeedbackService.showSnackBar.emit(sbSuccessSave)
                      },
                      positiveCallback: async ()=> { }
                    }
                  )
                }
              }
                
            }else{
              this.injectAppropriateView(settingId, undefined, undefined)
            }
          }
          
        } else{
          this.injectAppropriateView(settingId, values, form)
        }
      } else {
        if(this.currentState==1){
          if(this.data_saved){
            // this.navigateToBaseSelect()
            window.location.reload()

          }else{
            const sbSuccessSave = this.translateService.instant('save_data_first')
            const title = this.translateService.instant('title_unsaved_changes')
            const message = this.translateService.instant('msg_unsaved_changes')

            if( this.updateRight ) {
              this.userFeedbackService.showDialog.emit({
                entryComponent: GenericDialogComponent,
                title,
                message,
                cancleCallback: async () => {
                  window.location.reload()
                  const sbSuccessSave = this.translateService.instant('changes_dismissed')
                  this.userFeedbackService.showSnackBar.emit(sbSuccessSave)
                },
                positiveCallback: async () => { }
              })
            }
          }
        }else{
          let {settingId, values, form} = this.navigationPath[0]
          this.currentState = SettingsHandlerRoutes.BASE
          this.injectAppropriateView(settingId, values, form)
        }    
      }
    }
  }

  addSettingItem() {
    if (this.isSubContext) return

    this.newCreation=true
    if (this.currentState == SettingsHandlerRoutes.BASE || this.currentState ==  SettingsHandlerRoutes.FORM) return
    const length = this.navigationPath.length
    const settingId = this.navigationPath[length-1].settingId
    if (!settingId) return 
    let form: BaseSettingForm|undefined = undefined
    if (settingId == NetworkConstants.SETTING_UTILIZATION_OPTION) {
      const person = this.personService.personSubject$.value
      form = new BaseSettingForm("", settingId, undefined, person)
      form.componentRef = UtilizationOptionFormComponent

    } else if (settingId == NetworkConstants.SETTING_SPECIES) {
      form = new BaseSettingForm("", settingId, undefined)
      form.componentRef = SettingSpeciesFormComponent
    } else if (settingId == NetworkConstants.SETTING_GEO_TYPE) {
      form = new PoiTypeSettingForm("", settingId, undefined)
    } else if (settingId == NetworkConstants.SETTING_EVENT_INVITATION_SITE_REFERRAL) {
      const v = this.contextSettings.get(NetworkConstants.SETTING_EVENT_INVITATION_SITE_REFERRAL)?.setting?.values
      const existingValues = v ? v : []
      form = new SiteReferralSettingForm("", settingId, existingValues,undefined)
    } else if (settingId == NetworkConstants.SETTING_GEO_LINE_TYPES) {
      form = new gOLineTypesSettingForm("", settingId, undefined)

    } else if (settingId == NetworkConstants.SETTING_GEO_POLYGON_TYPES) {
      form = new gOPolygonTypesSettingForm("", settingId, undefined)

    }
     else if (settingId == NetworkConstants.SETTING_USER_ROLES) {
       form = new usRolesSettingForm("", settingId, undefined)
    } // NAR-5647
    else if (settingId == NetworkConstants.SETTING_GEO_CATEGORY) { //settingPoiCategory
      form = new gOCategorySettingForm("", settingId, undefined)
    } else {
      form = new BaseSettingForm("", settingId, undefined)
    }

    form.addListener(this)      
    form.onCreate = (data: any) => {
      const setting = this.contextSettings.get(settingId).setting
      if (!setting.values) {
        setting.values = [data]
      } else {
        this.contextSettings.get(settingId).setting.values.push(data) 
      }
      this.saveSetting(settingId, false)
    }
    this.navigationPath.push({settingId, values: undefined, form})
    this.injectAppropriateView(settingId, undefined, form)
  }

  // callbacks:
  onClick(data: any) {
    this.data_saved=false
    const {setting} = data.data
    switch(setting?.identifier) {
      case NetworkConstants.SETTING_GR_STATUS:
      case NetworkConstants.SETTING_GR_LOCATION:
      case NetworkConstants.SETTING_GR_SHOT_QUALITY: 
      case NetworkConstants.SETTING_HUNTING_METHODS:
      case NetworkConstants.SETTING_LAB_SAMPLE_LOCATION:
      case NetworkConstants.SETTING_LAB_SAMPLE_STATUS:
      case NetworkConstants.SETTING_LAB_SAMPLE_TYPE:
      case NetworkConstants.SETTING_TROPY_TYPE:
      case NetworkConstants.SETTING_UTILIZATION_OPTION:
      case NetworkConstants.SETTING_GEO_TYPE:
      case NetworkConstants.SETTING_GEO_CATEGORY:{
        let listItem: SelectableListItem[] = []
        if(setting.values){
          setting.values.forEach((element: any) => {
            const item = new SelectableListItem(
              element.title,
              "",
              element,
              NavListItemComponent
            )
            item.addListener(this)
            item.displayDelete = true
            item.onDeleteListItem = (identifier: string) => {
              this.onDeleteListItem(identifier, setting.identifier)
            }
            listItem.push(item)
          })
        }
        this.navigationPath.push({settingId: setting.identifier, values: listItem, form: undefined})
        this.injectAppropriateView(setting.identifier, listItem, undefined)
        break
      }
      case NetworkConstants.SETTING_GEO_LINE_TYPES:{
        let listItem: SelectableListItem[] = []
        if(setting.values){

        setting.values.forEach((element: any) => {
          const item = new SelectableListItem(
            element.title,
            "",
            element,
            NavListItemComponent
          )
          item.addListener(this)
          item.displayDelete = true
          item.onDeleteListItem = (identifier: string) => {
            this.onDeleteListItem(identifier, setting.identifier)
          }
          listItem.push(item)
        })
      }
        this.navigationPath.push({settingId: setting.identifier, values: listItem, form: undefined})
        this.injectAppropriateView(setting.identifier, listItem, undefined)
        break
      }
        
      case NetworkConstants.SETTING_GEO_POLYGON_TYPES:{
        let listItem: SelectableListItem[] = []
        if(setting.values){

          setting.values.forEach((element: any) => {
            const item = new SelectableListItem(
              element.title,
              "",
              element,
              NavListItemComponent
            )
            item.addListener(this)
            item.displayDelete = true
            item.onDeleteListItem = (identifier: string) => {
              this.onDeleteListItem(identifier, setting.identifier)
            }
            listItem.push(item)
          })
        }
        this.navigationPath.push({settingId: setting.identifier, values: listItem, form: undefined})
        this.injectAppropriateView(setting.identifier, listItem, undefined)
        break
      }
        
      case NetworkConstants.SETTING_GEO_USAGE_STATUS:
      case NetworkConstants.SETTING_GR_MEAT_QUALITY:
      case NetworkConstants.SETTING_READOUT_GROUPS:
      // NAR-5647
      case NetworkConstants.SETTING_USER_ROLES: {
        let listItem: SelectableListItem[] = []

        if(setting.values){
          setting.values.forEach((element: any) => {
            const item = new SelectableListItem(
              element.title,
              "",
              element,
              NavListItemComponent
            )
            item.addListener(this)
            item.displayDelete = true
            item.onDeleteListItem = (identifier: string) => {
              this.onDeleteListItem(identifier, setting.identifier)
            }
            listItem.push(item)
          })
        }

        this.navigationPath.push({settingId: setting.identifier, values: listItem, form: undefined})
        this.injectAppropriateView(setting.identifier, listItem, undefined)
        break
      }
      case NetworkConstants.SETTING_EVENT_INVITATION_SITE_REFERRAL: {
        let listItem: SelectableListItem[] = []
        if(setting.values){
          setting.values.forEach(async (element) => {
            let sub: string|undefined = undefined
            if (setting.identifier == NetworkConstants.SETTING_UTILIZATION_OPTION) {
              if (element.personPickerMode) {
                sub = await this.translateService.instant(`FORM.utilizationOptionForm.options.sub_${element.personPickerMode}`)
              } else {
                sub = await this.translateService.instant(`FORM.utilizationOptionForm.options.sub_none`)
              }
            }
            const item = new SelectableListItem(
              element.title,
              "",
              element,
              NavListItemComponent,
              sub
            )
            item.addListener(this)
            item.displayDelete = true
            item.onDeleteListItem = (identifier: string) => {
              this.onDeleteListItem(identifier, setting.identifier)
            }
            listItem.push(item)
          })
        }
        this.navigationPath.push({settingId: setting.identifier, values: listItem, form: undefined})
        this.injectAppropriateView(setting.identifier, listItem, undefined)
        break
      }
      case NetworkConstants.SETTING_COLLECTIVE_SPECIES: {
        let listItem: SelectableListItem[] = []
        if(setting.values){

          setting.values.forEach((element: any) => {
            const item = new SelectableListItem(
              element.names?.de,
              "",
              element,
              NavListItemComponent
            )
            item.addListener(this)
            item.displayDelete = true
            item.onDeleteListItem = (identifier: string) => {
              this.onDeleteListItem(identifier, setting.identifier)
            }
            listItem.push(item)
          })
        }
        this.navigationPath.push({settingId: setting.identifier, values: listItem, form: undefined})
        this.injectAppropriateView(setting.identifier, listItem, undefined)
        break
      }
      case NetworkConstants.SETTING_SPECIES: {
        let listItem: SelectableListItem[] = []
        if(setting.values){
          setting.values.forEach((element: any) => {
          let title: string = element.names.de
          let sub: string|undefined = undefined
          const item = new SelectableListItem(
            title,
            "",
            element,
            NavListItemComponent,
            sub
          )
          item.addListener(this)
          item.displayDelete = true
          item.onDeleteListItem = (identifier: string) => {
            this.onDeleteListItem(identifier, setting.identifier)
          }
          listItem.push(item)
        });  
        }
        
        this.navigationPath.push({settingId: setting.identifier, values: listItem, form: undefined})
        this.injectAppropriateView(setting.identifier, listItem, undefined)      
        break
      }
      default: {
        if (this.navigationPath.length >= 2) {
          let settingId = this.navigationPath[1].settingId
          if(!settingId){
            settingId = this.navigationPath[this.navigationPath.length-1].settingId
          }

          let person: Person[]|undefined = undefined
          if (settingId == NetworkConstants.SETTING_UTILIZATION_OPTION) {
            person = this.personService.personSubject$.value
          }
          // get form ... 
          switch(this.contextSettings.get(settingId).formRef) {
            case BaseSettingForm: {
              // create base form 
              const form = new BaseSettingForm("", settingId, data.data, person)
              if (settingId == NetworkConstants.SETTING_UTILIZATION_OPTION) {
                form.componentRef = UtilizationOptionFormComponent
              }
              if(settingId == NetworkConstants.SETTING_SPECIES) {
                form.componentRef = SettingSpeciesFormComponent
              }
              form.addListener(this)
              form.onSubmit = () => {
                this.saveSetting(settingId, false)
              }
              form.onDelete = async (data) => {
                const title = await this.translateService.instant('delete_title')
                const message = await this.translateService.instant('delete_message')
                this.userFeedbackService.showDialog.emit(
                  {
                    entryComponent: GenericDialogComponent,
                    title,
                    message,
                    cancleCallback: async () => {
                      const sbChangesDismissed = await this.translateService.instant('delete_canceled')
                      this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
                    },
                    positiveCallback: async ()=> {
                      this.userFeedbackService.loading.emit(true)
                      await this.onDeleteListItem(data.data.identifier, settingId)
                      this.userFeedbackService.loading.emit(false)
                    }
                  }
                ) 
              }
              this.navigationPath.push({settingId, values: undefined, form})
              this.injectAppropriateView(settingId, undefined, form)
              break
            }
            case (PoiTypeSettingForm): {
              const form = new PoiTypeSettingForm(
                "", settingId, data.data
              )
              form.addListener(this)
              form.onSubmit = () => {
                this.saveSetting(settingId, false)
              }

              form.onOpenIconPicker=() => { }

              form.onDelete = async (data) => {
                const title = await this.translateService.instant('delete_title')
                const message = await this.translateService.instant('delete_message')
                this.userFeedbackService.showDialog.emit(
                  {
                    entryComponent: GenericDialogComponent,
                    title,
                    message,
                    cancleCallback: async () => {
                      const sbChangesDismissed = await this.translateService.instant('delete_canceled')
                      this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
                    },
                    positiveCallback: async ()=> {
                      this.userFeedbackService.loading.emit(true)
                      await this.onDeleteListItem(data.data.identifier, settingId)
                      this.userFeedbackService.loading.emit(false)
                    }
                  }
                ) 
              }
              this.navigationPath.push({settingId, values: undefined, form})
              this.injectAppropriateView(settingId, undefined, form)
              break
            }
            case (SiteReferralSettingForm): {
              const { values } = 
                this.contextSettings.get(settingId)?.setting
              const existingValues = values ? values : []
              const form = new SiteReferralSettingForm(
                "", settingId, existingValues, data.data
              )
              form.addListener(this)
              form.onSubmit = () => {
                this.saveSetting(settingId, false)
              }

              form.onDelete = async (data) => {
                const title = await this.translateService.instant('delete_title')
                const message = await this.translateService.instant('delete_message')
                this.userFeedbackService.showDialog.emit(
                  {
                    entryComponent: GenericDialogComponent,
                    title,
                    message,
                    cancleCallback: async () => {
                      const sbChangesDismissed = await this.translateService.instant('delete_canceled')
                      this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
                    },
                    positiveCallback: async ()=> {
                      this.userFeedbackService.loading.emit(true)
                      await this.onDeleteListItem(data.data.identifier, settingId)
                      this.userFeedbackService.loading.emit(false)
                    }
                  }
                ) 
              }
              this.navigationPath.push({settingId, values: undefined, form})
              this.injectAppropriateView(settingId, undefined, form)
              break
            }
            case (gOLineTypesSettingForm): {
              // create geoObjectLineTypesSettingForm form 
              const form= new gOLineTypesSettingForm("", settingId, data.data)
              form.addListener(this)
              form.onSubmit = () => {
                this.saveSetting(settingId, false)
              }

              form.onDelete = async (data) => {
                const title = await this.translateService.instant('delete_title')
                const message = await this.translateService.instant('delete_message')
                this.userFeedbackService.showDialog.emit(
                  {
                    entryComponent: GenericDialogComponent,
                    title,
                    message,
                    cancleCallback: async () => {
                      const sbChangesDismissed = await this.translateService.instant('delete_canceled')
                      this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
                    },
                    positiveCallback: async ()=> {
                      this.userFeedbackService.loading.emit(true)
                      await this.onDeleteListItem(data.data.identifier, settingId)
                      this.userFeedbackService.loading.emit(false)
                    }
                  }
                ) 
              }

              this.navigationPath.push({settingId, values: undefined, form})
              this.injectAppropriateView(settingId, undefined, form)
              break
            }
            case (gOPolygonTypesSettingForm): {
              const form= new gOPolygonTypesSettingForm("", settingId, data.data)
              form.addListener(this)
              form.onSubmit = () => {
                this.saveSetting(settingId, false)
              }

              form.onDelete = async (data) => {
                const title = await this.translateService.instant('delete_title')
                const message = await this.translateService.instant('delete_message')
                this.userFeedbackService.showDialog.emit(
                  {
                    entryComponent: GenericDialogComponent,
                    title,
                    message,
                    cancleCallback: async () => {
                      const sbChangesDismissed = await this.translateService.instant('delete_canceled')
                      this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
                    },
                    positiveCallback: async ()=> {
                      this.userFeedbackService.loading.emit(true)
                      await this.onDeleteListItem(data.data.identifier, settingId)
                      this.userFeedbackService.loading.emit(false)
                    }
                  }
                ) 
              }

              this.navigationPath.push({settingId, values: undefined, form})
              this.injectAppropriateView(settingId, undefined, form)
              break
            }

            case (gOCategorySettingForm): {
              const form= new gOCategorySettingForm("", settingId, data.data)
              form.addListener(this)
              form.onSubmit = () => {
                this.saveSetting(settingId, false)
              }

              form.onDelete = async (data) => {
                const title = await this.translateService.instant('delete_title')
                const message = await this.translateService.instant('delete_message')
                this.userFeedbackService.showDialog.emit(
                  {
                    entryComponent: GenericDialogComponent,
                    title,
                    message,
                    cancleCallback: async () => {
                      const sbChangesDismissed = await this.translateService.instant('changes_dismissed')
                      this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
                    },
                    positiveCallback: async ()=> {
                      this.userFeedbackService.loading.emit(true)

                      await this.onDeleteListItem(data.data.identifier, settingId)
                      this.userFeedbackService.loading.emit(false)
                    }
                  }
                ) 
              }

              this.navigationPath.push({settingId, values: undefined, form})

              this.injectAppropriateView(settingId, undefined, form)
              break
            }
            // NAR-5647
            case (usRolesSettingForm): {
              const form = new usRolesSettingForm( "", settingId, data.data )
              form.addListener(this)
              form.onSubmit = () => {
                this.saveSetting(settingId, false)
              }
              form.onDelete = async (data) => {
                const title = await this.translateService.instant('delete_title')
                const message = await this.translateService.instant('delete_message')
                this.userFeedbackService.showDialog.emit({
                  entryComponent: GenericDialogComponent,
                  title,
                  message,
                  cancleCallback: async () => {
                    const sbChangesDismissed = await this.translateService.instant('delete_canceled')
                    this.userFeedbackService.showSnackBar.emit(sbChangesDismissed)
                  },
                  positiveCallback: async ()=> {
                    this.userFeedbackService.loading.emit(true)
                    await this.onDeleteListItem(data.data.identifier, settingId)
                    this.userFeedbackService.loading.emit(false)
                  }
                }) 
              }
              this.navigationPath.push({settingId, values: undefined, form})
              this.injectAppropriateView(settingId, undefined, form)
              break
            }
            default: {
              // form not implemented... 
              break
            }
          }
        }
        // get form for this thing... 
        break
      }
    } 
  }

  async saveSetting(settingId: string, del: boolean|undefined) {
    if (this.isSubContext) return
    try {
      this.userFeedbackService.loading.emit(true)
      await this.settingsService.updateSetting(this.oldSetting, this.contextSettings.get(settingId).setting)
      this.userFeedbackService.loading.emit(false)
      const sbSuccessSave = await this.translateService.instant('changes_saved')
      this.userFeedbackService.showSnackBar.emit(sbSuccessSave)
      this.data_saved=true
      this.newCreation=false
      let listitem = undefined
    
      if (del) {
        // update deletions in view ... 

        this.data_saved=false

        this.navigationPath.pop()
        this.navigationPath.pop()
        const listItem = this.updateListItems(settingId)
        listitem = listItem
        listItem.map(el => {
          el.displayDelete = true
          el.onDeleteListItem = (identifier) => {
            this.onDeleteListItem(identifier, settingId)
          }
        })
        this.navigationPath.push({settingId, values: listItem, form: undefined})
        this.injectAppropriateView(settingId, listItem, undefined)
      }else{
        setTimeout(() => {
         listitem = this.updateListItems(settingId)
          this.navigationPath.pop()
          this.navigationPath.pop()
          this.navigationPath.push({settingId, values: listitem, form: undefined})
          this.injectAppropriateView(settingId, listitem, undefined)
        }, 600)
      }
    } catch (error) {
      this.userFeedbackService.loading.emit(false)
      this.userFeedbackService.showDialog.emit(
        {
          entryComponent: GenericDialogComponent,
          title: "Error",
          message: error.error.message ? error.error.message : JSON.stringify(error),
        }
      )
    }
  }

  updateListItems(settingId): SelectableListItem[] {
    const setting = this.contextSettings.get(settingId).setting
    let listItem: SelectableListItem[] = []
    setting.values.forEach(async (el) => {
      let t: string|undefined = undefined
      let sub: string|undefined = undefined
      if (settingId == NetworkConstants.SETTING_COLLECTIVE_SPECIES) {
        t = el.names?.de
      } else if (settingId == NetworkConstants.SETTING_SPECIES) {
        t = el.names?.de
      } else {
        t = el.title 
      }
      // if (settingId == NetworkConstants.SETTING_UTILIZATION_OPTION) { // hide subtitle when updading item
      //   if (el.personPickerMode) {
      //     sub = await this.translateService.get(`FORM.utilizationOptionForm.options.sub_${el.personPickerMode}`).toPromise()
      //   } else {
      //     sub = await this.translateService.get(`FORM.utilizationOptionForm.options.sub_none`).toPromise()
      //   }
      // }
      const item = new SelectableListItem(
        t,
        "",
        el,
        NavListItemComponent,
        sub
      )
      item.addListener(this)
      item.displayDelete = true
      item.onDeleteListItem = (identifier: string) => {
        this.onDeleteListItem(identifier, settingId)
      }
      listItem.push(item)
    })
    return listItem
  }

  async observeData(): Promise<undefined> {
    await this.settingsService.settingShotQualitySubject.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingShotQualitySubject.pipe(
      skipWhile((el) => !el)
    ).subscribe((sett: IContextSetting) => {
      if (!sett) return 
      this.contextSettings.get(NetworkConstants.SETTING_GR_SHOT_QUALITY).setting = sett
      const ind = this.navigationPath.findIndex(el => el.settingId == sett.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(sett.identifier)
      // this.updateNavigationStack(shotQuality)
    })
    await this.settingsService.settingMeatQualitySubject.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingMeatQualitySubject.pipe(
      skipWhile((el) => !el)
    ).subscribe((sett: IContextSetting) => {
      if (!sett) return 
      this.contextSettings.get(NetworkConstants.SETTING_GR_MEAT_QUALITY).setting = sett
      const ind = this.navigationPath.findIndex(el => el.settingId == sett.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(sett.identifier)
    })
    
    await this.settingsService.settingsGameLocations.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingsGameLocations.pipe(
      skipWhile((el) => !el)
    ).subscribe((sett: IContextSetting) => {
      if (!sett) return
      this.contextSettings.get(NetworkConstants.SETTING_GR_LOCATION).setting = sett
      const ind = this.navigationPath.findIndex(el => el.settingId == sett.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(sett.identifier)
    })
    
    await this.settingsService.settingsGameStatus.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingsGameStatus.pipe(
      skipWhile((el) => !el)
    ).subscribe((sett: IContextSetting) => {
      if (!sett) return
      this.contextSettings.get(NetworkConstants.SETTING_GR_STATUS).setting = sett
      const ind = this.navigationPath.findIndex(el => el.settingId == sett.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(sett.identifier)
    })

    await this.settingsService.settingHuntingMethods.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingHuntingMethods.pipe(
      skipWhile((el) => !el)
    ).subscribe((sett: IContextSetting) => {
      if (!sett) return
      this.contextSettings.get(NetworkConstants.SETTING_HUNTING_METHODS).setting = sett
      const ind = this.navigationPath.findIndex(el => el.settingId == sett.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(sett.identifier)
    })

// -------------------------------------------------------

    await this.settingsService.settingGeoObjectLineType.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingGeoObjectLineType.pipe(
      skipWhile((el) => !el)
    ).subscribe((sett: IContextSetting) => {
      if (!sett) return
      this.contextSettings.get(NetworkConstants.SETTING_GEO_LINE_TYPES).setting = sett
      const ind = this.navigationPath.findIndex(el => el.settingId == sett.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(sett.identifier)
    })
// -------------------------------------------------------settingPoiCategory
    await this.settingsService.settingPoiCategory.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingPoiCategory.pipe(
      skipWhile((el) => !el)
    ).subscribe((sett: IContextSetting) => {
      if (!sett) return
      this.contextSettings.get(NetworkConstants.SETTING_GEO_CATEGORY).setting = sett
      const ind = this.navigationPath.findIndex(el => el.settingId == sett.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(sett.identifier)
    })

  // -------------------------------------------------------

    await this.settingsService.settingGeoObjectPolygonType.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingGeoObjectPolygonType.pipe(
      skipWhile((el) => !el)
    ).subscribe((sett: IContextSetting) => {
      if (!sett) return
      this.contextSettings.get(NetworkConstants.SETTING_GEO_POLYGON_TYPES).setting = sett
      const ind = this.navigationPath.findIndex(el => el.settingId == sett.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(sett.identifier)
    })
  // -------------------------------------------------------


    // await this.settingsService.settingsSampleLocation.pipe(skipWhile((el) => !el), take(1)).toPromise()
    // this.settingsService.settingsSampleLocation.pipe(
    //   skipWhile((el) => !el)
    // ).subscribe((obj: IContextSetting) => {
    //   if (!obj) return 
    //   this.contextSettings.get(NetworkConstants.SETTING_LAB_SAMPLE_LOCATION).setting = obj
    //   const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
    //   if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    // })

    await this.settingsService.settingsSampleStatus.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingsSampleStatus.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_LAB_SAMPLE_STATUS).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    await this.settingsService.settingsSampleType.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingsSampleType.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_LAB_SAMPLE_TYPE).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    await this.settingsService.settingCollectiveSpecies.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingCollectiveSpecies.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_COLLECTIVE_SPECIES).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    await this.settingsService.settingTrophyType.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingTrophyType.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_TROPY_TYPE).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    await this.settingsService.settingUtilizationOption.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingUtilizationOption.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_UTILIZATION_OPTION).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    await this.settingsService.settingSpecies.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingSpecies.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_SPECIES).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    await this.settingsService.settingPoiType.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingPoiType.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_GEO_TYPE).setting = obj
      // const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      // if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    await this.settingsService.settingGeoObjectUsageState.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingGeoObjectUsageState.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_GEO_USAGE_STATUS).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    await this.settingsService.settingEventInvitationSiteReferral.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingEventInvitationSiteReferral.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_EVENT_INVITATION_SITE_REFERRAL).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    await this.settingsService.settingReadoutGroup.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingReadoutGroup.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 
      this.contextSettings.get(NetworkConstants.SETTING_READOUT_GROUPS).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    // NAR-5647
    await this.settingsService.settingUserRoles.pipe(skipWhile((el) => !el), take(1)).toPromise()
    this.settingsService.settingUserRoles.pipe(
      skipWhile((el) => !el)
    ).subscribe((obj: IContextSetting) => {
      if (!obj) return 

      if(!obj.identifier)
      obj.identifier = NetworkConstants.SETTING_USER_ROLES

      if( this.contextSettings.get(NetworkConstants.SETTING_USER_ROLES))
       this.contextSettings.get(NetworkConstants.SETTING_USER_ROLES).setting = obj
      const ind = this.navigationPath.findIndex(el => el.settingId == obj.identifier)
      if (ind != -1) this.navigationPath[ind].values = this.updateListItems(obj.identifier)
    })

    return of(undefined).toPromise()
  }

  async onDeleteListItem(identifier, settingId) {
    if (this.isSubContext) return
    // delete list item 
    const setting = this.contextSettings.get(settingId)?.setting
    if (!setting) return 
    // check if species... 
    const ind = setting.values.findIndex(el => el.identifier == identifier)
    if (ind != -1) {
      try {
        await this.checkIfSaveToDelete(settingId, setting.values[ind])
        const set = this.contextSettings.get(settingId)
        set.setting.values.splice(ind, 1)
        this.saveSetting(settingId, true)
      } catch (error) {
        const title = await this.translateService.instant('dialog_delete_failed_title')
        const msg = await this.translateService.instant('dialog_delete_failed_msg')
        this.userFeedbackService.showDialog.emit(
          {
            entryComponent: GenericDialogComponent,
            title,
            message: msg,
          }
        )
      }
    }
  }

  private checkIfSaveToDelete(settingId: string, setting: any): Promise<void> {
    return new Promise(async (resolve, reject) => {
      switch(settingId) {
        case(NetworkConstants.SETTING_SPECIES): {
          const records = await this.gamerecordService.allGameRecordsSubject$
            .pipe(
              skipWhile(el => !el),
              take(1)
            ).toPromise()
          if (records.map(el => el.wildart).includes(setting.identifier)) {
            reject()
          } else {
            resolve()
          }
        }
        case(NetworkConstants.SETTING_GEO_TYPE): {
          const records = await this.poiService.poiSubject$
            .pipe(
              skipWhile(el => !el),
              take(1)
            ).toPromise()
          if (records.map(el => el.type).includes(setting.identifier)) {
            reject()
          } else {
            resolve()
          }
        }
      }
      resolve()
    })
  }

  async openIconPicker(dataToChange) { }
}
